import { applyMiddleware, createStore } from 'redux';
import thunk from 'redux-thunk';
import { createRef } from 'react';

import { SET_FIELDS, SET_DISABLED, ADD_RESOURCE,
  SET_REQUIRED, SET_OBJECT, SET_SCROLL_POSITION } from 'constants/actionTypes';

import field from './fieldReducer';
import object from './objectReducer';
import modal, { initialModalState } from './modalReducer';
import massedit from './masseditReducer';
import view from './viewReducer';
import visibility from './visibilityReducer';


import update from 'immutability-helper';
import { setValue, addValue, removeValue, loadEnums } from 'actions/fieldActions';
import { getObjectFields } from 'helpers/objectHelpers';
import { editResource, createResource, 
  removeResource, addResourceValue,
  removeObjectLink } from 'actions/resourceActions';

const initialState = {
  modal: initialModalState
};

// Combines all reducers into one
// Passes modalOpen state to field & object reducer
const rootReducer = (state = initialState, action) => {
  const modalOpen = state.modal.showing;

  return {
    field: field(state.field, { ...action, modalOpen }),
    modal: modal(state.modal, action),
    object: object(state.object, { ...action, modalOpen }),
    massedit: massedit(state.massedit, action),
    view: view(state.view, action),
    visibility: visibility(state.visibility, action)
  };
};

const store = createStore(rootReducer, applyMiddleware(thunk));

/**
 * Field bindings to dispatch updates to Redux
*/ 
export const bindFieldToStore = (field) => {
  const predicate = field.predicate;
  const { dispatch, getState } = store;

  field.addValue = value => {
    dispatch(addValue(predicate, value));
  };
  field.removeValue = index => {
    dispatch(removeValue(predicate, index));
  };
  field.setValue = (index, value) => {
    dispatch(setValue(predicate, index, value));
  };
  field.addResourceValue = (value) => {
    dispatch(addResourceValue(value, predicate));
  };
  field.createResource = () => {
    createResource({ predicate })(dispatch, getState);
  };
  field.editResource = (index, value) => {
    editResource({ index, value, predicate })(dispatch, getState);
  };
  field.removeResource = (index, value) => {
    removeResource({ index, value, predicate })(dispatch, getState);
  };
  field.removeLink = (index, value) => {
    removeObjectLink({ value, predicate, index })(dispatch, getState);
  };
  field.setDisabled = (value) => {
    dispatch({ type: SET_DISABLED, value, predicate });
  };
  field.setRequired = (value) => {
    dispatch({ type: SET_REQUIRED, value, predicate });
  };
};

export const addFieldToStore = field => {
  const fields = update(store.getState().field.fields, {
    [field.predicate]: { $set: field }
  });

  store.dispatch({ type: SET_FIELDS, fields });
};

/**
 * Add bindings to object fields, adding methods to update Redux state
 * (field.setValue(...) -> update state in Redux)
 */
export const bindObjectToStore = (object, mapObject) => {
  const fields = getObjectFields(object.tabs);
  const fieldsMap = {};

  for (let field of fields) {
    field.required = false;
    field.disabled = false;
    bindFieldToStore(field);
    fieldsMap[field.predicate] = field;
  }

  // Function that may alter object before dispatching to Redux
  // eg. MassEdit may want to remove props.minOccurs from all fields.
  mapObject?.(object);

  store.dispatch(loadEnums(fields));
  store.dispatch({ type: SET_FIELDS, fields: fieldsMap });
  store.dispatch({ type: SET_OBJECT, object });
};

/**
 * Add bindings to Resource fields, adding methods to update Redux state
 * (field.setValue(...) -> update state in Redux)
 */
export const bindResourceToStore = (resource) => {
  const fieldsMap = {};

  resource = {
    ...resource,
    originalFields: resource.fields,
    validated: false,
    scrollPos: 0,
    ref: createRef()
  };

  for (let field of resource.fields) {
    field.required = false;
    field.disabled = false;
    field.inModal = true;
    bindFieldToStore(field);
    fieldsMap[field.predicate] = field;
  }

  resource.fields = fieldsMap;
  if (store.getState().modal.resources.length > 0) {
    store.dispatch({ type: SET_SCROLL_POSITION });
  }
  store.dispatch({ type: ADD_RESOURCE, resource });
};

export default store;